import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { computeNextWeekLoad, computeNextSetInSetLoad, computeMiniSetLoads } from "../../utils/progressionEngine";
import { simulateChain } from "../../utils/precompiledChain";
import { computeSetDivergence } from "../../utils/progressionDivergence";
import {
  computeHypLoad,
  computeHypRepsGrid as computeHypRepsGridShared,
  hypRepsDisplay,
  normalizePastedRule,
  resolveSourceSlot,
  ruleIsEmpty,
} from "../../utils/precompiledRuleApply";
import { DragDropContext, Draggable, DraggableProvidedDragHandleProps, DropResult } from "react-beautiful-dnd";
import StrictModeDroppable from "../../common/StrictModeDroppable";
import { useToasts } from "../../contexts/toasts";
import {
  PrecompiledProgression,
  PrecompiledWeek,
  PrecompiledWeekProgressionRule,
  ProgressionMethodConfig,
  PrecompiledSet,
  SogliaThreshold,
  SogliaItem,
  SetItem,
  RepScheme,
  Effort,
  MiniSet,
} from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { useEntityOptions } from "../../hooks/useEntityOptions";
import { useClipboard } from "../../contexts/clipboard";
import { normalizePrecompiledProgression } from "../../utils/precompiledNormalizer";
import { Alert, Button as BsButton, Col, Container, Modal as BsModal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlus,
  faSave,
  faTrash,
  faChevronDown,
  faRepeat,
  faDumbbell,
  faArrowTrendUp,
  faArrowTrendDown,
  faInfoCircle,
  faCopy,
  faPaste,
  faGripVertical,
  faChartLine,
  faStopwatch,
  faTimeline,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import { AutoComplete, Form, Input, InputNumber, Select, Tag } from "antd";
import CollapsibleSection from "../../Components/Form/CollapsibleSection";
import DecimalInputNumber from "../../common/DecimalInputNumber";
import ProgressionRuleEditor from "../../Components/Form/ProgressionRuleEditor";
import StickySaveBar from "../../Components/StickySaveBar";
import "./PrecompiledProgressions.css";

// ─── helpers ─────────────────────────────────────────────────────────────────

const emptyRule = (): PrecompiledWeekProgressionRule => ({
  type: "fixed",
  loadComputeSource: null,
  initialLoadType: "fixed",
  initialLoad: null,
  initialWnRm: null,
  initialPnRm: null,
  weekPriority: null,
  incrementDirection: null,
  incrementTarget: null,
  repsChange: null,
  loadChangeType: null,
  loadChangeValue: null,
  weekWnRm: null,
  weekPnRm: null,
  progressionType: null,
  positiveThresholdsStS: null,
  negativeThresholdsStS: null,
  positiveThresholdsSiS: null,
  negativeThresholdsSiS: null,
  matrixSource: null,
  calculationPriority: null,
  weekPrioritySiS: null,
  incrementDirectionSiS: null,
  incrementTargetSiS: null,
  repsChangeSiS: null,
  loadChangeTypeSiS: null,
  loadChangeValueSiS: null,
  weekWnRmSiS: null,
  weekPnRmSiS: null,
  matrixSourceSiS: null,
});

// FE-Q (#873010fd) — used by the rule paste-confirm gate. A rule is
// considered "non-empty" if it deviates from emptyRule() in any field that
// the user could have intentionally set (rule type, computation source,
// thresholds, increment fields, etc.). `type: "fixed"` and
// `initialLoadType: "fixed"` are emptyRule() defaults — those alone do not
// count as user input.
function isRuleNonEmpty(rule: PrecompiledWeekProgressionRule | null | undefined): boolean {
  if (!rule) return false;
  const e = emptyRule();
  for (const key of Object.keys(rule) as (keyof PrecompiledWeekProgressionRule)[]) {
    const a = (rule as any)[key];
    const b = (e as any)[key];
    if (Array.isArray(a)) {
      if (a.length > 0) return true;
      continue;
    }
    if (a !== undefined && a !== null && a !== b) return true;
  }
  return false;
}

const emptyWeek = (weekNumber: number): PrecompiledWeek => ({
  weekNumber,
  repSchemeTypeId: 0,
  repScheme: "",
  effortTypeId: 0,
  effort: "",
  tutMode: "select",
  tut: null,
  tecnicaMode: "select",
  tecnica: null,
  xRepsTarget: null,
  xRepsLoadDropPerc: null,
  progressionRule: emptyRule(),
  sets: [],
});

const emptySet = (): PrecompiledSet => ({
  label: null,
  trialLoad: null,
  progressionRule: emptyRule(),
});

function remapSet(prev: Set<number>, from: number, to: number): Set<number> {
  const result = new Set<number>();
  prev.forEach((idx) => {
    if (idx === from) result.add(to);
    else if (from < to && idx > from && idx <= to) result.add(idx - 1);
    else if (from > to && idx >= to && idx < from) result.add(idx + 1);
    else result.add(idx);
  });
  return result;
}

function remapRecord<T>(prev: Record<number, T>, from: number, to: number): Record<number, T> {
  const result: Record<number, T> = {};
  Object.entries(prev).forEach(([k, v]) => {
    const idx = parseInt(k);
    let newIdx: number;
    if (idx === from) newIdx = to;
    else if (from < to && idx > from && idx <= to) newIdx = idx - 1;
    else if (from > to && idx >= to && idx < from) newIdx = idx + 1;
    else newIdx = idx;
    result[newIdx] = v;
  });
  return result;
}


// ─── Field helpers ────────────────────────────────────────────────────────────

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="pp-field-group">
      <span className="pp-field-label">{label}</span>
      {children}
    </div>
  );
}

function ModeToggle({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string }[];
}) {
  return (
    <div className="pp-mode-toggle">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={value === o.value ? "active" : ""}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── RepsValueInput ───────────────────────────────────────────────────────────
// FE-3 (#ae917a1e) — free-form reps cell that accepts a single integer ("8")
// or a range ("8-10" / "8–10"). Validates against the same regex the engine
// uses (FE-1 isRepsRange / getSogliaSchemeValue). Persists trimmed string when
// the value contains "-"/"–", number when it's a single integer.

const REPS_VALUE_PATTERN = /^\d+(\s*[-–]\s*\d+)?$/;

function RepsValueInput({
  value,
  onChange,
}: {
  value: number | string | null | undefined;
  onChange: (v: number | string | null) => void;
}) {
  const [draft, setDraft] = useState<string>(
    value == null ? "" : String(value),
  );
  useEffect(() => {
    setDraft(value == null ? "" : String(value));
  }, [value]);

  const trimmed = draft.trim();
  const isEmpty = trimmed === "";
  const isValid = isEmpty || REPS_VALUE_PATTERN.test(trimmed);

  const commit = () => {
    if (isEmpty) {
      onChange(null);
      return;
    }
    if (!isValid) return;
    if (/[-–]/.test(trimmed)) {
      onChange(trimmed);
    } else {
      const n = parseInt(trimmed, 10);
      onChange(Number.isFinite(n) ? n : null);
    }
  };

  return (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
      status={!isValid ? "error" : undefined}
      addonAfter="reps"
      style={{ width: 130 }}
      placeholder="reps o range"
    />
  );
}

// ─── ThresholdList ────────────────────────────────────────────────────────────

function ThresholdList({
  label,
  thresholds,
  onChange,
  incrementTarget,
  loadChangeType,
}: {
  label: string;
  thresholds: SogliaThreshold[];
  onChange: (v: SogliaThreshold[]) => void;
  incrementTarget: "reps" | "load" | "both";
  loadChangeType: "percentage" | "kg" | null;
}) {
  const add = () =>
    onChange([...thresholds, { threshold: 1, loadValue: null, repsValue: null }]);
  const remove = (i: number) => onChange(thresholds.filter((_, j) => j !== i));
  const update = (i: number, partial: Partial<SogliaThreshold>) => {
    const next = [...thresholds];
    next[i] = { ...next[i], ...partial };
    onChange(next);
  };

  const showLoad = incrementTarget === "load" || incrementTarget === "both";
  const showReps = incrementTarget === "reps" || incrementTarget === "both";

  return (
    <div className="pp-threshold-list">
      <div className="pp-threshold-list-label">
        <span>{label}</span>
        <button type="button" className="pp-threshold-add-btn" onClick={add}>
          <FontAwesomeIcon icon={faPlus} /> Soglia
        </button>
      </div>
      {thresholds.length === 0 && (
        <div className="pp-threshold-empty">Nessuna soglia configurata</div>
      )}
      {thresholds.map((t, i) => (
        <div key={i} className="pp-threshold-row">
          <span className="pp-threshold-label">Δ reps ≥</span>
          <InputNumber
            value={t.threshold}
            onChange={(v) => update(i, { threshold: v ?? 0 })}
            min={0}
            style={{ width: 72 }}
          />
          <span className="pp-threshold-arrow">→</span>
          {showLoad && (
            <InputNumber
              value={t.loadValue ?? undefined}
              onChange={(v) => update(i, { loadValue: v ?? null })}
              min={0}
              addonAfter={loadChangeType === "percentage" ? "%" : "kg"}
              style={{ width: 110 }}
              placeholder="valore"
            />
          )}
          {showReps && (
            <RepsValueInput
              value={t.repsValue}
              onChange={(v) => update(i, { repsValue: v })}
            />
          )}
          <button
            type="button"
            className="pp-threshold-remove"
            onClick={() => remove(i)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Week2 method field-groups ────────────────────────────────────────────────

function computeIncrementPreview(
  baseLoad: number,
  direction: "increment" | "decrement" | null | undefined,
  loadChangeType: "percentage" | "kg" | null | undefined,
  loadChangeValue: number | null | undefined,
): number | null {
  if (!direction || !loadChangeValue) return null;
  const sign = direction === "decrement" ? -1 : 1;
  const unit = loadChangeType ?? "kg";
  if (unit === "kg") return Math.round((baseLoad + sign * loadChangeValue) * 100) / 100;
  return Math.round(baseLoad * (1 + sign * loadChangeValue / 100) * 100) / 100;
}


// ─── SetMiniCard helpers ──────────────────────────────────────────────────────
// FE-A (#a4c1a4b4) — applyRuleStS / applyRuleSiS extracted to
// `src/utils/precompiledRuleApply.ts` and shared with the detail view + chain.


// Wave 3.1 (#583bd67b) — threshold-aware inheritance helper.
// When the previous slot's rule.type === "thresholds" AND it has persisted
// workReps + workLoad (Wave 1.3), project via the engine's threshold path
// using the same SogliaThreshold→SogliaItem adapter as the per-set futuro.
// `target` selects the StS or SiS branch.
// FE-C (#546b25bd) — applyRuleThresholdInherit extracted to
// `src/utils/precompiledRuleApply.ts`.


// PROG-ENGINE-V2/3 (Bug 3 + Q3=yes dedupe) — computeHypLoad now lives in
// precompiledRuleApply.ts and is imported above. The shared implementation
// reads `effectiveLoadComputeSource(rule)` so a fully-configured StS/SiS rule
// with null explicit `loadComputeSource` (set via per-tab "Carico Fisso"
// without touching Section-A's Sorgente Select) dispatches via the
// progressionType-derived branch immediately — no save+reopen required.

// PROG-BUGS/ENGINE-3 (Bug A) — the local `computeHypReps(repScheme)` helper
// that just stripped the leading `\d+x` from the week-level repScheme has
// been replaced by the shared `computeHypReps` / `hypRepsDisplay` pair from
// `precompiledRuleApply.ts`. The shared helper is rule-aware (sts / sis /
// both / threshold / fixed_increment-with-reps-target) so the header pill
// and the body's "Ipotetico (da sorgente)" badge come from the same engine
// path. `hypRepsDisplay` falls back to the week-level repScheme (stripped
// of `\d+x`) when the rule-driven projection is null — preserving the
// legacy display for source slots and load-only rules.

// ─── SetMiniCard ──────────────────────────────────────────────────────────────

function SetMiniCard({
  set,
  setIndex,
  isFirstWeek,
  onUpdate,
  onRemove,
  onCopy,
  onPaste,
  canPaste,
  onCopyRule,
  onPasteRule,
  canPasteRule,
  copiedBoxConfig,
  onCopyBoxConfig,
  dragHandleProps,
  weekRepScheme,
  weekFallback,
  repSchemeTypeOptions,
  effortTypeOptions,
  setRepSchemeOptions,
  setEffortOptions,
  tutOptions,
  tecnicaOptions,
  onSetRepSchemeTypeChange,
  onSetEffortTypeChange,
  prevWeekHypLoad,
  prevWeekSetRule,
  prevSetHypLoad,
  prevSetRule,
  prevWeekSet,
  prevSetInWeek,
  hypLoad,
  hypRepsProjected,
  tecnicheRaw,
  weeks,
  weekIndex,
}: {
  set: PrecompiledSet;
  setIndex: number;
  isFirstWeek: boolean;
  onUpdate: (partial: Partial<PrecompiledSet>) => void;
  onRemove: () => void;
  /** F3 — copy this set to the precompiled-set clipboard slot. */
  onCopy?: () => void;
  /** F3 — paste the clipboard's set onto this one (overwrites all params). */
  onPaste?: () => void;
  /** F3 — true when the clipboard has a precompiled-set to paste. */
  canPaste?: boolean;
  /** FE-Q — copy this set's progression rule (whole rule, includes soglie). */
  onCopyRule?: () => void;
  /** FE-Q — paste the clipboard's rule onto this set; intrinsic params kept. */
  onPasteRule?: () => void;
  /** FE-Q — true when the clipboard has a precompiled-rule to paste. */
  canPasteRule?: boolean;
  /** Per-box clipboard slot (canonical StS/SiS config) forwarded to the editor. */
  copiedBoxConfig?: ProgressionMethodConfig | null;
  /** Copy a StS/SiS box config to the per-box clipboard slot. */
  onCopyBoxConfig?: (config: ProgressionMethodConfig, kind: "sts" | "sis") => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  weekRepScheme?: string | null;
  /** F1 — week-level fallback values rendered when the per-set field is unset. */
  weekFallback?: {
    repSchemeTypeId?: number | null;
    repScheme?: string | null;
    effortTypeId?: number | null;
    effort?: string | null;
    tutMode?: "select" | "free" | null;
    tut?: string | null;
    tecnicaMode?: "select" | "free" | null;
    tecnica?: string | null;
  };
  // PROG-05 F1b — cascading Selects.
  repSchemeTypeOptions: { label: string; value: string | number }[];
  effortTypeOptions: { label: string; value: string | number }[];
  setRepSchemeOptions?: { label: string; value: string }[];
  setEffortOptions?: { label: string; value: string }[];
  tutOptions: { label: string; value: string | number }[];
  tecnicaOptions: { label: string; value: string | number }[];
  onSetRepSchemeTypeChange: (typeId: number) => void;
  onSetEffortTypeChange: (typeId: number) => void;
  // PROG-ENGINE-V3 Step B (#06918ce6) — prev-slot props are kept for the
  // inline ProgressionRuleEditor's `computePrevSlotProjection` preview (FE-C
  // #546b25bd), which still needs the load/rule axis. The chip itself no
  // longer recomputes; it reads `hypLoad` / `hypRepsProjected` resolved by
  // the parent grid (single source of truth, walk-back-aware).
  prevWeekHypLoad?: number | null;
  prevWeekSetRule?: PrecompiledWeekProgressionRule | null;
  prevSetHypLoad?: number | null;
  prevSetRule?: PrecompiledWeekProgressionRule | null;
  prevWeekSet?: PrecompiledSet | null;
  prevSetInWeek?: PrecompiledSet | null;
  hypLoad: number | null;
  hypRepsProjected: number | string | null;
  // Wave 3.2 (#f309a975) — Tecnica catalog so the set can decide whether to
  // surface the mini-sets block when the chosen Tecnica has includeXReps && dropXReps.
  tecnicheRaw?: { id?: number; name: string; includeXReps?: boolean; dropXReps?: boolean }[];
  // PROG-ENGINE-V3/4 (#0fffbe95) — full weeks + this card's weekIndex thread
  // through so ProgressionRuleEditor can enumerate ancestor sources for the
  // multi-source picker.
  weeks?: PrecompiledWeek[];
  weekIndex?: number;
}) {
  const [open, setOpen] = useState(false);
  // FE-Q (#873010fd) — confirm-paste modal state. Open when the user clicks
  // "Incolla regola" while the current rule is non-empty (i.e. paste would
  // overwrite real configuration).
  const [showRulePasteConfirm, setShowRulePasteConfirm] = useState(false);
  // Wave 1.3 (#90f3f635) — work data is now persisted on PrecompiledSet.
  // Read from the set; write through onUpdate to the form's mutate channel.
  const workReps = set.workReps != null ? String(set.workReps) : "";
  const workLoad = set.workLoad != null ? String(set.workLoad) : "";
  const rule = set.progressionRule ?? emptyRule();
  // F1 — per-set values; fall back to week-level when unset (back-compat).
  // The normalizer adapter (coming in a follow-up commit) will copy
  // week-level → per-set on read so legacy plans render uniformly. Until
  // then, this fallback ensures opening an existing plan does not show
  // empty per-set fields.
  const effRepScheme = set.repScheme ?? weekFallback?.repScheme ?? null;
  const effEffort = set.effort ?? weekFallback?.effort ?? null;
  const effTut = set.tut ?? weekFallback?.tut ?? null;
  const effTecnica = set.tecnica ?? weekFallback?.tecnica ?? null;
  // PROG-ENGINE-V3 Step B (#06918ce6) — chip reads grid-resolved values from
  // props (single source of truth, walk-back-aware). Local recompute via
  // computeHypLoad / computeHypRepsShared removed; the parent `weekHypLoads`
  // and `weekHypReps` memos resolve via the shared engine and pass the row
  // through `currentWeekHypLoads` / `currentWeekHypReps` to WeekCard.
  const hypReps = hypRepsDisplay(hypRepsProjected, weekRepScheme);
  // PROG-ENGINE-V2/5 (#d55fc4ee) — advisory divergence pill. Compares the
  // executed work data on this set to the engine's suggested values
  // (hypLoad/hypReps). Renders only when the result is non-null (i.e. at
  // least one axis is out of tolerance: >5% load, exact reps mismatch).
  const divergence = computeSetDivergence(set, hypLoad, hypReps);

  return (
    <div className="pp-set-mini-card">
      <div className="pp-set-mini-header" onClick={() => setOpen((p) => !p)}>
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            onClick={(e) => e.stopPropagation()}
            className="pp-drag-handle"
          >
            <FontAwesomeIcon icon={faGripVertical} />
          </span>
        )}
        <div className="pp-set-mini-num">{setIndex + 1}</div>
        <Input
          value={set.label ?? ""}
          onChange={(e) => onUpdate({ label: e.target.value || null })}
          onClick={(e) => e.stopPropagation()}
          placeholder={`Set ${setIndex + 1}`}
          style={{ flex: 1 }}
        />
        {/* #9a40b43c — gate the collapsed-card hyp chips identically to the
            open-card template badge (#da50d997): on non-first-set-first-week
            slots, show the load/reps chips ONLY when the user has picked an
            explicit Sorgente carico (`loadComputeSource ∈ {sts, sis, both}`).
            The `divergence` chip is an independent advisory signal — keep it
            visible when present regardless of source pick. */}
        {(() => {
          if (open) return null;
          const showSrcChips =
            (isFirstWeek && setIndex === 0)
              || rule.loadComputeSource === "sts"
              || rule.loadComputeSource === "sis"
              || rule.loadComputeSource === "both";
          const renderLoad = showSrcChips && hypLoad != null;
          const renderReps = showSrcChips && hypReps != null;
          const renderDivergence = divergence != null;
          if (!renderLoad && !renderReps && !renderDivergence) return null;
          return (
            <div className="pp-set-hyp-chips" onClick={(e) => e.stopPropagation()}>
              {renderLoad && (
                <span className="pp-set-hyp-chip pp-set-hyp-chip-load">{hypLoad} kg</span>
              )}
              {renderReps && (
                <span className="pp-set-hyp-chip pp-set-hyp-chip-reps">{hypReps} reps</span>
              )}
              {renderDivergence && (
                <span
                  className="pp-set-hyp-chip pp-set-hyp-chip-divergence"
                  title="Esecuzione differisce dal suggerito (>5% kg o reps differenti)"
                >
                  ⚠
                  {divergence!.load === "above" && " ↑kg"}
                  {divergence!.load === "below" && " ↓kg"}
                  {divergence!.reps === "above" && " ↑rep"}
                  {divergence!.reps === "below" && " ↓rep"}
                </span>
              )}
            </div>
          );
        })()}
        {onCopy && (
          <button
            type="button"
            className="pp-week-delete"
            title="Copia set"
            onClick={(e) => { e.stopPropagation(); onCopy(); }}
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
        )}
        {onPaste && (
          <button
            type="button"
            className="pp-week-delete"
            title={canPaste ? "Incolla set" : "Nessun set copiato"}
            disabled={!canPaste}
            style={canPaste ? undefined : { opacity: 0.4, cursor: "not-allowed" }}
            onClick={(e) => { e.stopPropagation(); if (canPaste) onPaste(); }}
          >
            <FontAwesomeIcon icon={faPaste} />
          </button>
        )}
        <button
          type="button"
          className="pp-week-delete"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`pp-week-chevron${open ? " open" : ""}`}
        />
      </div>
      {open && (
        <div className="pp-set-mini-body">
          {/* FE-N (#84fcb586) — Level-1 collapsible, default CLOSED. */}
          <CollapsibleSection
            title="Parametri di allenamento"
            icon={faDumbbell}
            level={1}
            defaultOpen={false}
          >
          <div className="pp-subsection">
            <Row className="g-2">
              <Col xs={12} sm={6}>
                <FieldGroup label="Carico di riferimento (kg)">
                  <InputNumber
                    className="w-100"
                    value={set.trialLoad ?? undefined}
                    onChange={(v) => onUpdate({ trialLoad: v ?? null })}
                    min={0}
                    step={0.01}
                    precision={2}
                    placeholder="—"
                  />
                </FieldGroup>
              </Col>
          {/* PROG-05 F1b — cascading per-set Selects mirroring the week-level
              UX (`PrecompiledProgressionForm.tsx:1501+`). Each parent Select
              (Type) unlocks its child (Scheme/Effort) once a value is chosen;
              the child uses a per-set catalog filtered by the per-set typeId.
              Per-set values fall back to week-level when null; the "Eredita:"
              chip surfaces that fallback. Clearing a parent typeId nulls the
              corresponding scheme/effort so it gets re-selected from the new
              catalog. tutMode / tecnicaMode also follow the week-level mode
              when per-set mode is unset. */}
            <Col xs={12} sm={6}>
              <FieldGroup label="Rep Scheme Type">
                <Select
                  className="w-100"
                  options={repSchemeTypeOptions}
                  value={set.repSchemeTypeId ?? undefined}
                  onChange={(val) => {
                    const typeId = (val as number | null | undefined) ?? null;
                    onUpdate({ repSchemeTypeId: typeId, repScheme: null });
                    if (typeId) onSetRepSchemeTypeChange(typeId);
                  }}
                  placeholder={
                    weekFallback?.repSchemeTypeId
                      ? "Eredita dalla settimana"
                      : "Seleziona tipo..."
                  }
                  allowClear
                />
                {set.repSchemeTypeId == null && weekFallback?.repSchemeTypeId != null && (
                  <span className="pp-set-inherit-chip">Eredita tipo settimana</span>
                )}
              </FieldGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FieldGroup label="Rep Scheme">
                <AutoComplete
                  className="w-100"
                  options={setRepSchemeOptions ?? []}
                  value={set.repScheme ?? undefined}
                  onChange={(val) => onUpdate({ repScheme: (val as string | null) || null })}
                  placeholder={
                    set.repSchemeTypeId
                      ? "Seleziona o digita (es. 8-12)"
                      : "Prima seleziona il tipo"
                  }
                  disabled={!set.repSchemeTypeId}
                  allowClear
                  filterOption={(input, opt) =>
                    String(opt?.label ?? opt?.value ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
                {set.repScheme == null && effRepScheme != null && (
                  <span className="pp-set-inherit-chip">Eredita: {effRepScheme}</span>
                )}
              </FieldGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FieldGroup label="Effort Type">
                <Select
                  className="w-100"
                  options={effortTypeOptions}
                  value={set.effortTypeId ?? undefined}
                  onChange={(val) => {
                    const typeId = (val as number | null | undefined) ?? null;
                    onUpdate({ effortTypeId: typeId, effort: null });
                    if (typeId) onSetEffortTypeChange(typeId);
                  }}
                  placeholder={
                    weekFallback?.effortTypeId
                      ? "Eredita dalla settimana"
                      : "Seleziona tipo..."
                  }
                  allowClear
                />
                {set.effortTypeId == null && weekFallback?.effortTypeId != null && (
                  <span className="pp-set-inherit-chip">Eredita tipo settimana</span>
                )}
              </FieldGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FieldGroup label="Effort">
                <Select
                  className="w-100"
                  options={setEffortOptions ?? []}
                  value={set.effort ?? undefined}
                  onChange={(val) => onUpdate({ effort: (val as string | null) ?? null })}
                  placeholder={
                    set.effortTypeId
                      ? "Seleziona effort..."
                      : "Prima seleziona il tipo"
                  }
                  disabled={!set.effortTypeId}
                  allowClear
                />
                {set.effort == null && effEffort != null && (
                  <span className="pp-set-inherit-chip">Eredita: {effEffort}</span>
                )}
              </FieldGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FieldGroup label="Time Under Tension">
                <ModeToggle
                  value={set.tutMode ?? weekFallback?.tutMode ?? "select"}
                  onChange={(v) =>
                    onUpdate({ tutMode: v as "select" | "free", tut: null })
                  }
                  options={[
                    { label: "Seleziona", value: "select" },
                    { label: "Libero", value: "free" },
                  ]}
                />
                {(set.tutMode ?? weekFallback?.tutMode ?? "select") === "select" ? (
                  <Select
                    className="w-100"
                    options={tutOptions}
                    value={set.tut != null ? Number(set.tut) : undefined}
                    onChange={(val) =>
                      onUpdate({ tut: val != null ? String(val) : null })
                    }
                    placeholder="Seleziona TUT..."
                    allowClear
                  />
                ) : (
                  <Input
                    value={set.tut ?? ""}
                    onChange={(e) => onUpdate({ tut: e.target.value || null })}
                    placeholder={weekFallback?.tut ?? "es. 3-1-2-0"}
                  />
                )}
                {set.tut == null && effTut != null && (
                  <span className="pp-set-inherit-chip">Eredita: {effTut}</span>
                )}
              </FieldGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FieldGroup label="Tecnica">
                <ModeToggle
                  value={set.tecnicaMode ?? weekFallback?.tecnicaMode ?? "select"}
                  onChange={(v) =>
                    onUpdate({
                      tecnicaMode: v as "select" | "free",
                      tecnica: null,
                    })
                  }
                  options={[
                    { label: "Seleziona", value: "select" },
                    { label: "Libero", value: "free" },
                  ]}
                />
                {(set.tecnicaMode ?? weekFallback?.tecnicaMode ?? "select") === "select" ? (
                  <Select
                    className="w-100"
                    options={tecnicaOptions}
                    value={set.tecnica != null ? Number(set.tecnica) : undefined}
                    onChange={(val) =>
                      onUpdate({
                        tecnica: val != null ? String(val) : null,
                      })
                    }
                    placeholder="Seleziona tecnica..."
                    allowClear
                  />
                ) : (
                  <Input
                    value={set.tecnica ?? ""}
                    onChange={(e) => onUpdate({ tecnica: e.target.value || null })}
                    placeholder={weekFallback?.tecnica ?? "es. Drop Set"}
                  />
                )}
                {set.tecnica == null && effTecnica != null && (
                  <span className="pp-set-inherit-chip">Eredita: {effTecnica}</span>
                )}
              </FieldGroup>
            </Col>
            </Row>
          </div>
          </CollapsibleSection>

          {/* FE-N (#84fcb586) — Level-1 collapsible, default CLOSED. */}
          <CollapsibleSection
            title="Regola progressione"
            icon={faChartLine}
            level={1}
            defaultOpen={false}
          >
          <div className="pp-subsection">
            {/* FE-Q (#873010fd) — whole-rule copy/paste actions.
                FE-S (#4c36fc9c) — `pp-rule-action-btn` (auto-width, balanced
                padding, neutral palette) replaces the previous fixed-size
                `pp-week-delete` icon button which produced overlapping/
                unbalanced labels. Equal flex on the wrapper keeps the two
                buttons the same width on every viewport. */}
            {(onCopyRule || onPasteRule) && (
              <div className="pp-rule-actions">
                {onCopyRule && (
                  <button
                    type="button"
                    className="pp-rule-action-btn"
                    title="Copia regola progressione"
                    onClick={(e) => { e.stopPropagation(); onCopyRule(); }}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                    <span>Copia regola</span>
                  </button>
                )}
                {onPasteRule && (
                  <button
                    type="button"
                    className="pp-rule-action-btn"
                    title={canPasteRule ? "Incolla regola progressione" : "Nessuna regola copiata"}
                    disabled={!canPasteRule}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canPasteRule) return;
                      if (isRuleNonEmpty(rule)) {
                        setShowRulePasteConfirm(true);
                      } else {
                        onPasteRule();
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faPaste} />
                    <span>Incolla regola</span>
                  </button>
                )}
              </div>
            )}
            <BsModal show={showRulePasteConfirm} onHide={() => setShowRulePasteConfirm(false)} centered>
              <BsModal.Header closeButton>
                <BsModal.Title>Sostituire la regola corrente?</BsModal.Title>
              </BsModal.Header>
              <BsModal.Body>
                La regola di progressione attuale verrà sostituita dalla regola copiata
                (incluse le eventuali soglie). Questa azione non può essere annullata.
              </BsModal.Body>
              <BsModal.Footer>
                <BsButton variant="secondary" onClick={() => setShowRulePasteConfirm(false)}>
                  Annulla
                </BsButton>
                <BsButton
                  variant="primary"
                  onClick={() => {
                    setShowRulePasteConfirm(false);
                    if (onPasteRule) onPasteRule();
                  }}
                >
                  Sostituisci
                </BsButton>
              </BsModal.Footer>
            </BsModal>
            <ProgressionRuleEditor
              rule={rule}
              isFirstWeek={isFirstWeek}
              setIndex={setIndex}
              weekRepScheme={weekRepScheme}
              onChange={(p) => onUpdate({ progressionRule: { ...rule, ...p } })}
              prevWeekHypLoad={prevWeekHypLoad}
              prevWeekSetRule={prevWeekSetRule}
              prevSetHypLoad={prevSetHypLoad}
              prevSetRule={prevSetRule}
              prevWeekSet={prevWeekSet}
              prevSetInWeek={prevSetInWeek}
              weeks={weeks}
              wi={weekIndex}
              si={setIndex}
              gridHypLoad={hypLoad}
              gridHypReps={hypReps}
              copiedBoxConfig={copiedBoxConfig}
              onCopyBoxConfig={onCopyBoxConfig}
            />
            {/* #da50d997 — on non-first-set slots, only render the template
                badge once the user has picked a "Sorgente carico" (an
                explicit `loadComputeSource ∈ {sts, sis, both}`). Without a
                source pick there is no axis to label and the grid value
                doesn't reflect a user-intent yet. The first-set-first-week
                slot keeps the legacy gate so its initial-load baseline
                ("Carico ipotetico (template)") still shows under the
                "Tipologia carico iniziale" / initialLoad UX where the
                "Sorgente carico" Select isn't surfaced. */}
            {(hypLoad != null || hypReps != null) &&
              (isFirstWeek && setIndex === 0
                ? true
                : rule.loadComputeSource === "sts"
                  || rule.loadComputeSource === "sis"
                  || rule.loadComputeSource === "both") && (
              <div className="pp-calc-result">
                <span className="pp-calc-result-label">
                  {/* #453ca577 — reflect the source axis on the dest set's
                      template badge when the rule is sts/sis/both; fall back
                      to the legacy "(template)" suffix for fixed/dynamic/null. */}
                  {rule.loadComputeSource === "sts"
                    ? "Carico ipotetico (set to set)"
                    : rule.loadComputeSource === "sis"
                    ? "Carico ipotetico (set in set)"
                    : rule.loadComputeSource === "both"
                    ? "Carico ipotetico (set to set + set in set)"
                    : "Carico ipotetico (template)"}
                </span>
                <div className="pp-calc-badges">
                  {hypLoad != null && (
                    <span className="pp-calc-badge pp-calc-badge-load">{hypLoad} kg</span>
                  )}
                  {hypReps != null && (
                    <span className="pp-calc-badge pp-calc-badge-reps">{hypReps} reps</span>
                  )}
                </div>
              </div>
            )}
          </div>
          </CollapsibleSection>
          {/* Work-data section — persisted on PrecompiledSet (#90f3f635).
              Per #d646de3c, shows the next-week/set "futuro" projection driven
              by the soglie (threshold) progression rule, gated on rule.type === "thresholds". */}
          <div className="pp-subsection">
            <div className="pp-subsection-title">
              <FontAwesomeIcon icon={faStopwatch} className="me-1" />
              Calcolo da work data
            </div>
          <Row className="g-2">
            <Col xs={12} sm={6}>
              <FieldGroup label="Work Reps">
                <Input
                  value={workReps}
                  onChange={(e) => {
                    const v = e.target.value;
                    onUpdate({ workReps: v === "" ? null : v });
                  }}
                  placeholder="Reps eseguite"
                />
              </FieldGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FieldGroup label="Work Load">
                <DecimalInputNumber
                  value={set.workLoad ?? null}
                  onChange={(v) =>
                    onUpdate({ workLoad: v == null ? null : Number(v) })
                  }
                  style={{ width: "100%" }}
                  placeholder="Carico (kg)"
                />
              </FieldGroup>
            </Col>
            {(() => {
              const wr = parseFloat(workReps);
              const wl = parseFloat(workLoad);
              const hasWork = !isNaN(wr) && !isNaN(wl) && wr > 0 && wl > 0;
              if (!hasWork) return null;
              const pt = rule.progressionType ?? null;
              if (pt === "none" || pt == null) return null;
              // Wave 2.1 (#0063f565) — per-confronto rule.type. SiS falls back to
              // rule.type when typeSiS is unset (back-compat).
              const ruleKindStS = rule.type;
              const ruleKindSiS = rule.typeSiS ?? rule.type;
              const isStS = (pt === "set-to-set" || pt === "both") && ruleKindStS === "thresholds";
              const isSiS = (pt === "set-in-set" || pt === "both") && ruleKindSiS === "thresholds";
              if (!isStS && !isSiS) return null;
              // Map precompiled "loadChangeType" ("percentage"|"kg") onto the engine's
              // ProgressionRuleType. "kg" → "weight" (additive); "percentage" or unset → "%".
              // Per #ef46a4be: this drives whether applyRule treats the matched soglia's
              // value as a kg delta or a percentage of base load.
              const ruleTypeStS: "%" | "weight" =
                rule.loadChangeType === "kg" ? "weight" : "%";
              const ruleTypeSiS: "%" | "weight" =
                rule.loadChangeTypeSiS === "kg" ? "weight" : "%";
              // Reuse engine soglie logic by synthesizing a SetItem with the
              // precompiled thresholds mapped onto SogliaItem[]. The soglia's `type`
              // mirrors the rule's so applyRule's switch picks the right branch.
              const toSoglie = (
                ts: SogliaThreshold[] | null | undefined,
                t: "%" | "weight",
              ): SogliaItem[] =>
                (ts ?? []).map((th) => ({
                  threshold: th.threshold,
                  type: t,
                  value: th.loadValue ?? 0,
                }));
              const synthStS: SetItem = {
                intensityType: "%",
                progressionType: "set-to-set",
                calculationPriority: "soglie",
                workLoad: String(wl),
                repScheme: effRepScheme ?? undefined,
                incrementRuleStS: { type: ruleTypeStS, value: 0, soglie: toSoglie(rule.positiveThresholdsStS, ruleTypeStS) },
                decrementRuleStS: { type: ruleTypeStS, value: 0, soglie: toSoglie(rule.negativeThresholdsStS, ruleTypeStS) },
              };
              const synthSiS: SetItem = {
                intensityType: "%",
                progressionType: "set-in-set",
                calculationPriority: "soglie",
                workLoad: String(wl),
                repScheme: effRepScheme ?? undefined,
                incrementRuleSiS: { type: ruleTypeSiS, value: 0, soglie: toSoglie(rule.positiveThresholdsSiS, ruleTypeSiS) },
                decrementRuleSiS: { type: ruleTypeSiS, value: 0, soglie: toSoglie(rule.negativeThresholdsSiS, ruleTypeSiS) },
              };
              const futuroStS = isStS ? computeNextWeekLoad(synthStS, wr) : undefined;
              const futuroSiS = isSiS ? computeNextSetInSetLoad(synthSiS, wr) : undefined;
              const ipoteticoFuturo = futuroStS ?? futuroSiS;
              const futuroLabel = futuroStS !== undefined ? "Sett. succ." : futuroSiS !== undefined ? "Set succ." : undefined;
              if (ipoteticoFuturo === undefined) return null;
              return (
                <Col xs={24} sm={12}>
                  <div className="pp-calc-result" style={{ marginTop: 0 }}>
                    <span className="pp-calc-result-label ppd-set-field-label--futuro">
                      Carico ipotetico futuro (da work data) {futuroLabel ? `— ${futuroLabel}` : ""}
                    </span>
                    <div className="pp-calc-badges">
                      <span className="pp-calc-badge pp-calc-badge-load">
                        {ipoteticoFuturo} kg
                      </span>
                    </div>
                  </div>
                </Col>
              );
            })()}
          </Row>
          </div>

          {/* Drop-set / mini-set editor. Surfaced whenever the chosen Tecnica
              includes X-Reps (previously also required dropXReps). */}
          {(() => {
            const setTec = effTecnica;
            const tec = (tecnicheRaw ?? []).find(
              (t) => t.name === setTec || String(t.id) === String(setTec),
            );
            if (!tec || !tec.includeXReps) return null;
            const miniSets: MiniSet[] = set.miniSets ?? [];
            const baseLoad =
              (set.workLoad != null ? Number(set.workLoad) : 0) ||
              (set.trialLoad != null ? Number(set.trialLoad) : 0);
            const projections = computeMiniSetLoads(baseLoad, miniSets);
            const updateMini = (i: number, patch: Partial<MiniSet>) =>
              onUpdate({
                miniSets: miniSets.map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
              });
            const addMini = () =>
              onUpdate({
                miniSets: [...miniSets, { reps: "", deltaType: "%", deltaValue: 0 }],
              });
            const removeMini = (i: number) =>
              onUpdate({ miniSets: miniSets.filter((_, idx) => idx !== i) });
            return (
              <div className="pp-subsection">
                <div className="pp-subsection-title">
                  <FontAwesomeIcon icon={faLayerGroup} className="me-1" />
                  Mini-set (drop set)
                </div>
                {miniSets.map((m, i) => {
                  const proj = projections[i];
                  return (
                    <Row key={i} className="g-2" align="middle" style={{ marginBottom: 6 }}>
                      <Col xs={6} sm={4}>
                        <FieldGroup label={i === 0 ? "Reps" : ""}>
                          <Input
                            value={String(m.reps ?? "")}
                            onChange={(e) => updateMini(i, { reps: e.target.value })}
                            placeholder="es. 8 o 6-8"
                          />
                        </FieldGroup>
                      </Col>
                      <Col xs={5} sm={4}>
                        <FieldGroup label={i === 0 ? "Tipo" : ""}>
                          <Select
                            className="w-100"
                            value={m.deltaType}
                            onChange={(val) =>
                              updateMini(i, { deltaType: val as "%" | "kg" })
                            }
                            options={[
                              { label: "%", value: "%" },
                              { label: "kg", value: "kg" },
                            ]}
                          />
                        </FieldGroup>
                      </Col>
                      <Col xs={5} sm={3}>
                        <FieldGroup label={i === 0 ? "Delta" : ""}>
                          <InputNumber
                            className="w-100"
                            min={0}
                            value={m.deltaValue}
                            onChange={(v) =>
                              updateMini(i, { deltaValue: typeof v === "number" ? v : 0 })
                            }
                          />
                        </FieldGroup>
                      </Col>
                      <Col xs={8} sm={4}>
                        {proj && proj.load > 0 && (
                          <span
                            className="pp-calc-badge pp-calc-badge-load"
                            style={{ fontSize: 12 }}
                          >
                            {proj.load} kg
                          </span>
                        )}
                      </Col>
                      <Col xs={16} sm={1}>
                        <button
                          type="button"
                          className="pp-week-delete"
                          title="Rimuovi mini-set"
                          onClick={() => removeMini(i)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </Col>
                    </Row>
                  );
                })}
                <button
                  type="button"
                  className="pp-add-week-btn"
                  style={{ marginTop: 4 }}
                  onClick={addMini}
                >
                  <FontAwesomeIcon icon={faPlus} /> Aggiungi mini-set
                </button>
              </div>
            );
          })()}

          {/* Wave 3.4 (#3974928e) — full hypothetical chain summary. Single
              source of truth for what the engine projects from this set.
              #972f8e54 — pass the real `weeks` array + `(weekIndex, setIndex)`
              so simulateChain's V3/1 walk-back at precompiledChain.ts:147-167
              can resolve a dependent set's source. The legacy 1-week stub
              starved walk-back and silently rendered an empty chain on
              dependents whose rule body only carried a dispatch hint. */}
          {(() => {
            const chain =
              weeks != null && weekIndex != null
                ? simulateChain(weeks, weekIndex, setIndex, { tecnicheRaw })
                : simulateChain(
                    [{
                      weekNumber: 1,
                      repSchemeTypeId: 0,
                      repScheme: weekRepScheme ?? "",
                      effortTypeId: 0,
                      effort: "",
                      tutMode: "free" as const,
                      tut: null,
                      tecnicaMode: "free" as const,
                      tecnica: null,
                      xRepsTarget: null,
                      xRepsLoadDropPerc: null,
                      progressionRule: rule,
                      sets: [set],
                    }],
                    0,
                    0,
                    { tecnicheRaw },
                  );
            if (chain.length === 0) return null;
            const groups: Record<string, typeof chain> = { main: [] };
            chain.forEach((c) => {
              if (c.kind === "main") groups.main.push(c);
            });
            return (
              <div className="pp-subsection">
                <div className="pp-subsection-title">
                  <FontAwesomeIcon icon={faTimeline} className="me-1" />
                  Catena ipotetici
                </div>
                {(["main"] as const).map((k) => {
                  if (groups[k].length === 0) return null;
                  // FE-E (#286e266b) — split main into stacked Carico/Reps rows.
                  const loadBadges = groups[k].filter((c) => c.load != null);
                  const repsBadges = groups[k].filter((c) => c.reps != null);
                  return (
                    <div key={k} style={{ marginBottom: 6 }}>
                      <div className="pp-chain-group-title">Principale</div>
                      {loadBadges.length > 0 && (
                        <div className="pp-chain-row" style={{ marginBottom: 4 }}>
                          <span className="pp-chain-row-label" style={{ minWidth: 44 }}>Carico</span>
                          {loadBadges.map((c, i) => (
                            <span key={`main-load-${i}`} className="pp-calc-badge pp-calc-badge-load pp-calc-badge--sm">
                              {c.label}: {c.load} kg
                            </span>
                          ))}
                        </div>
                      )}
                      {repsBadges.length > 0 && (
                        <div className="pp-chain-row">
                          <span className="pp-chain-row-label" style={{ minWidth: 44 }}>Reps</span>
                          {repsBadges.map((c, i) => (
                            <span key={`main-reps-${i}`} className="pp-calc-badge pp-calc-badge-reps pp-calc-badge--sm">
                              {c.label.replace(" (reps)", "")} × {c.reps} reps
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ─── WeekCard ─────────────────────────────────────────────────────────────────

interface WeekCardProps {
  week: PrecompiledWeek;
  index: number;
  total: number;
  open: boolean;
  onToggle: (index: number) => void;
  onUpdate: (index: number, partial: Partial<PrecompiledWeek>) => void;
  onRemove: (index: number) => void;
  /** F3 — copy this week to the precompiled-week clipboard slot. */
  onCopy?: (index: number) => void;
  /** F3 — paste the clipboard's week onto this one (overwrites). */
  onPaste?: (index: number) => void;
  /** F3 — true when the clipboard has a precompiled-week to paste. */
  canPaste?: boolean;
  repSchemeOptions: { label: string; value: string }[];
  effortOptions: { label: string; value: string }[];
  tutOptions: { label: string; value: string | number }[];
  tecnicaOptions: { label: string; value: string | number }[];
  tecnicheRaw: { id?: number; name: string; includeXReps?: boolean; dropXReps?: boolean }[];
  repSchemeTypeOptions: { label: string; value: string | number }[];
  effortTypeOptions: { label: string; value: string | number }[];
  onRepSchemeTypeChange: (index: number, typeId: number) => void;
  onEffortTypeChange: (index: number, typeId: number) => void;
  // PROG-05 F1b — per-set catalogs (filtered by per-set typeId) and the
  // per-set type-change handlers. Keyed by setIdx within this week.
  setRepSchemes?: Record<number, { label: string; value: string }[]>;
  setEfforts?: Record<number, { label: string; value: string }[]>;
  onSetRepSchemeTypeChange: (weekIdx: number, setIdx: number, typeId: number) => void;
  onSetEffortTypeChange: (weekIdx: number, setIdx: number, typeId: number) => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  prevWeekHypLoads?: (number | null)[];
  currentWeekHypLoads?: (number | null)[];
  prevWeekSets?: PrecompiledSet[] | null;
  // PROG-ENGINE-V3 Step B (#06918ce6) — current week reps row feeds each
  // SetMiniCard's `hypRepsProjected` prop (chip reads from grid). The
  // `prevWeekHypReps` grid was previously threaded for SetMiniCard's now-
  // removed prev-reps recompute and is no longer needed at this boundary.
  currentWeekHypReps?: (number | string | null)[];
  // PROG-ENGINE-V3/4 (#0fffbe95) — full weeks array threaded down to
  // SetMiniCard → ProgressionRuleEditor for the multi-source picker.
  weeks?: PrecompiledWeek[];
}

function WeekCard({
  week,
  index,
  total,
  open,
  onToggle,
  onUpdate,
  onRemove,
  onCopy,
  onPaste,
  canPaste,
  repSchemeOptions,
  effortOptions,
  tutOptions,
  tecnicaOptions,
  tecnicheRaw,
  repSchemeTypeOptions,
  effortTypeOptions,
  onRepSchemeTypeChange,
  onEffortTypeChange,
  setRepSchemes,
  setEfforts,
  onSetRepSchemeTypeChange,
  onSetEffortTypeChange,
  dragHandleProps,
  prevWeekHypLoads,
  currentWeekHypLoads,
  currentWeekHypReps,
  prevWeekSets,
  weeks,
}: WeekCardProps) {
  const isFirstWeek = index === 0;

  const [showUnified, setShowUnified] = useState(false);
  const [unifiedN, setUnifiedN] = useState(1);
  const [unifiedRule, setUnifiedRule] = useState<PrecompiledWeekProgressionRule>(emptyRule);

  // F3 PreFE — clipboard wiring for set-level copy/paste. Independent slot
  // (`copiedPrecompiledSet`) per the data-model split discussed in 420a89b9 —
  // do not bridge from the programmazione `copiedSetItem` slot, the shapes
  // diverge.
  const {
    copiedPrecompiledSet,
    copyPrecompiledSet,
    copiedPrecompiledRule,
    copyPrecompiledRule,
    copiedPrecompiledRuleBox,
    copyPrecompiledRuleBox,
  } = useClipboard();
  const { showToast } = useToasts();
  // Per-box Copia handler shared by every SetMiniCard + the unified editor in
  // this week. Stores the canonical config and toasts which box was copied.
  const handleCopyBoxConfig = (
    config: ProgressionMethodConfig,
    kind: "sts" | "sis",
  ) => {
    copyPrecompiledRuleBox(config);
    showToast(
      "pp-rule-box-copied",
      kind === "sts" ? "Box Set to Set copiato" : "Box Set in Set copiato",
      undefined,
      "success",
    );
  };

  const sets = week.sets ?? [];
  const addSet = () => onUpdate(index, { sets: [...sets, emptySet()] });
  const removeSet = (si: number) => onUpdate(index, { sets: sets.filter((_, j) => j !== si) });
  const updateSet = (si: number, partial: Partial<PrecompiledSet>) => {
    const next = [...sets];
    next[si] = { ...next[si], ...partial };
    onUpdate(index, { sets: next });
  };
  // F3 — overwrite the target set's params with the clipboard's content,
  // keeping the slot's clone-on-write semantics so subsequent edits don't
  // leak back into the clipboard.
  const pasteSet = (si: number) => {
    if (!copiedPrecompiledSet) return;
    const cloned: PrecompiledSet = JSON.parse(JSON.stringify(copiedPrecompiledSet));
    // FE-P (#0370f830) — back-fill `incrementTarget` / `loadChangeType` (and
    // SiS mirrors) when the pasted rule has thresholds but a gating field is
    // null, so the threshold rows render. No-op for clean rules.
    if (cloned.progressionRule) {
      cloned.progressionRule = normalizePastedRule(cloned.progressionRule);
    }
    const next = [...sets];
    next[si] = cloned;
    onUpdate(index, { sets: next });
  };
  // FE-Q (#873010fd) — paste only the future-progression rule (with its
  // soglie) onto a target set. Mirrors the legacy `copyProgressionRule`
  // action: target set's intrinsic params (label/repScheme/effort/work data)
  // are kept, only `progressionRule` is replaced. Reuses the FE-P
  // normalizePastedRule helper so render-guard gating fields are filled.
  const pasteRule = (si: number) => {
    if (!copiedPrecompiledRule) return;
    const cloned: PrecompiledWeekProgressionRule = normalizePastedRule(
      JSON.parse(JSON.stringify(copiedPrecompiledRule)),
    );
    const next = [...sets];
    next[si] = { ...next[si], progressionRule: cloned };
    onUpdate(index, { sets: next });
  };
  const applyUnified = () => {
    const newSets = Array.from({ length: unifiedN }, () => ({
      ...emptySet(),
      progressionRule: { ...unifiedRule },
    }));
    onUpdate(index, { sets: [...sets, ...newSets] });
    setShowUnified(false);
    setUnifiedN(1);
    setUnifiedRule(emptyRule());
  };

  // summary chips shown when collapsed
  const summaryChips: string[] = [];
  if (week.repScheme) summaryChips.push(week.repScheme);
  if (week.effort) summaryChips.push(week.effort);
  if (week.tut) summaryChips.push(`TUT ${week.tut}`);
  if (week.tecnica && week.tecnicaMode === "free") summaryChips.push(week.tecnica);
  if (sets.length > 0) summaryChips.push(`${sets.length} set`);

  return (
    <div className="pp-week-card">
      {/* header – always visible */}
      <div className="pp-week-card-header" onClick={() => onToggle(index)}>
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            onClick={(e) => e.stopPropagation()}
            className="pp-drag-handle"
          >
            <FontAwesomeIcon icon={faGripVertical} />
          </span>
        )}
        <div className="pp-week-num">
          {index + 1}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="pp-week-card-title">Settimana {index + 1}</div>
          {!open && summaryChips.length > 0 && (
            <div className="pp-week-card-summary">
              {summaryChips.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
          )}
        </div>

        {onCopy && (
          <button
            className="pp-week-delete"
            type="button"
            title="Copia settimana"
            onClick={(e) => { e.stopPropagation(); onCopy(index); }}
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
        )}
        {onPaste && (
          <button
            className="pp-week-delete"
            type="button"
            title={canPaste ? "Incolla settimana" : "Nessuna settimana copiata"}
            disabled={!canPaste}
            style={canPaste ? undefined : { opacity: 0.4, cursor: "not-allowed" }}
            onClick={(e) => { e.stopPropagation(); if (canPaste) onPaste(index); }}
          >
            <FontAwesomeIcon icon={faPaste} />
          </button>
        )}
        {total > 1 && (
          <button
            className="pp-week-delete"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        )}

        <FontAwesomeIcon
          icon={faChevronDown}
          className={`pp-week-chevron${open ? " open" : ""}`}
        />
      </div>

      {/* body – shown when open */}
      {open && (
        <div className="pp-week-body">
          {/* ─ Parametri allenamento ─ */}
          <div className="pp-subsection">
            <div className="pp-subsection-title">
              <FontAwesomeIcon icon={faDumbbell} className="me-1" />
              Parametri di allenamento
            </div>

            <Row className="g-2">
              <Col xs={12} sm={6}>
                <FieldGroup label="Rep Scheme Type">
                  <Select
                    className="w-100"
                    options={repSchemeTypeOptions}
                    value={week.repSchemeTypeId || undefined}
                    onChange={(val) => {
                      onUpdate(index, { repSchemeTypeId: val as number, repScheme: "" });
                      onRepSchemeTypeChange(index, val as number);
                    }}
                    placeholder="Seleziona tipo..."
                  />
                </FieldGroup>
              </Col>
              <Col xs={12} sm={6}>
                <FieldGroup label="Rep Scheme">
                  <AutoComplete
                    className="w-100"
                    options={repSchemeOptions}
                    value={week.repScheme || undefined}
                    onChange={(val) => onUpdate(index, { repScheme: (val as string) ?? "" })}
                    placeholder={week.repSchemeTypeId ? "Seleziona o digita (es. 8-12)" : "Prima seleziona il tipo"}
                    disabled={!week.repSchemeTypeId}
                    allowClear
                    filterOption={(input, opt) =>
                      String(opt?.label ?? opt?.value ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </FieldGroup>
              </Col>
              <Col xs={12} sm={6}>
                <FieldGroup label="Effort Type">
                  <Select
                    className="w-100"
                    options={effortTypeOptions}
                    value={week.effortTypeId || undefined}
                    onChange={(val) => {
                      onUpdate(index, { effortTypeId: val as number, effort: "" });
                      onEffortTypeChange(index, val as number);
                    }}
                    placeholder="Seleziona tipo..."
                  />
                </FieldGroup>
              </Col>
              <Col xs={12} sm={6}>
                <FieldGroup label="Effort">
                  <Select
                    className="w-100"
                    options={effortOptions}
                    value={week.effort || undefined}
                    onChange={(val) => onUpdate(index, { effort: val as string })}
                    placeholder={week.effortTypeId ? "Seleziona effort..." : "Prima seleziona il tipo"}
                    disabled={!week.effortTypeId}
                  />
                </FieldGroup>
              </Col>

              {/* TUT */}
              <Col xs={12} sm={6}>
                <FieldGroup label="Time Under Tension">
                  <ModeToggle
                    value={week.tutMode}
                    onChange={(v) =>
                      onUpdate(index, { tutMode: v as "select" | "free", tut: null })
                    }
                    options={[
                      { label: "Seleziona", value: "select" },
                      { label: "Libero", value: "free" },
                    ]}
                  />
                  {week.tutMode === "select" ? (
                    <Select
                      className="w-100"
                      options={tutOptions}
                      value={week.tut != null ? Number(week.tut) : undefined}
                      onChange={(val) => onUpdate(index, { tut: String(val) })}
                      placeholder="Seleziona TUT..."
                      allowClear
                    />
                  ) : (
                    <Input
                      value={week.tut ?? ""}
                      onChange={(e) => onUpdate(index, { tut: e.target.value || null })}
                      placeholder="es. 3-1-2-0"
                    />
                  )}
                </FieldGroup>
              </Col>

              {/* Tecnica */}
              <Col xs={12} sm={6}>
                <FieldGroup label="Tecnica">
                  <ModeToggle
                    value={week.tecnicaMode}
                    onChange={(v) =>
                      onUpdate(index, {
                        tecnicaMode: v as "select" | "free",
                        tecnica: null,
                        xRepsTarget: null,
                        xRepsLoadDropPerc: null,
                      })
                    }
                    options={[
                      { label: "Seleziona", value: "select" },
                      { label: "Libero", value: "free" },
                    ]}
                  />
                  {week.tecnicaMode === "select" ? (
                    <Select
                      className="w-100"
                      options={tecnicaOptions}
                      value={week.tecnica != null ? Number(week.tecnica) : undefined}
                      onChange={(val) =>
                        onUpdate(index, {
                          tecnica: val != null ? String(val) : null,
                          xRepsTarget: null,
                          xRepsLoadDropPerc: null,
                        })
                      }
                      placeholder="Seleziona tecnica..."
                      allowClear
                    />
                  ) : (
                    <Input
                      value={week.tecnica ?? ""}
                      onChange={(e) => onUpdate(index, { tecnica: e.target.value || null })}
                      placeholder="es. Drop Set"
                    />
                  )}
                </FieldGroup>
              </Col>
            </Row>
          </div>

          {/* ─ Set della settimana ─ */}
          <div className="pp-subsection" style={{ marginBottom: 0 }}>
            <div className="pp-subsection-title">
              <FontAwesomeIcon icon={faDumbbell} className="me-1" />
              Set ({sets.length})
            </div>

            <StrictModeDroppable droppableId={`sets-${index}`}>
              {(droppableProvided) => (
                <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                  {sets.map((s, si) => (
                    <Draggable key={si} draggableId={`set-${index}-${si}`} index={si}>
                      {(draggableProvided) => (
                        <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                          <SetMiniCard
                            set={s}
                            setIndex={si}
                            isFirstWeek={isFirstWeek}
                            weeks={weeks}
                            weekIndex={index}
                            onUpdate={(partial) => updateSet(si, partial)}
                            onRemove={() => removeSet(si)}
                            onCopy={() => copyPrecompiledSet(s)}
                            onPaste={() => pasteSet(si)}
                            canPaste={!!copiedPrecompiledSet}
                            onCopyRule={() => {
                              copyPrecompiledRule(s.progressionRule ?? emptyRule());
                              showToast("pp-rule-copied", "Regola copiata", undefined, "success");
                            }}
                            onPasteRule={() => pasteRule(si)}
                            canPasteRule={!!copiedPrecompiledRule}
                            copiedBoxConfig={copiedPrecompiledRuleBox}
                            onCopyBoxConfig={handleCopyBoxConfig}
                            dragHandleProps={draggableProvided.dragHandleProps}
                            weekRepScheme={week.repScheme}
                            weekFallback={{
                              repSchemeTypeId: week.repSchemeTypeId ?? null,
                              repScheme: week.repScheme ?? null,
                              effortTypeId: week.effortTypeId ?? null,
                              effort: week.effort ?? null,
                              tutMode: week.tutMode ?? null,
                              tut: week.tut ?? null,
                              tecnicaMode: week.tecnicaMode ?? null,
                              tecnica: week.tecnica ?? null,
                            }}
                            repSchemeTypeOptions={repSchemeTypeOptions}
                            effortTypeOptions={effortTypeOptions}
                            setRepSchemeOptions={setRepSchemes?.[si]}
                            setEffortOptions={setEfforts?.[si]}
                            tutOptions={tutOptions}
                            tecnicaOptions={tecnicaOptions}
                            onSetRepSchemeTypeChange={(typeId) =>
                              onSetRepSchemeTypeChange(index, si, typeId)
                            }
                            onSetEffortTypeChange={(typeId) =>
                              onSetEffortTypeChange(index, si, typeId)
                            }
                            prevWeekHypLoad={prevWeekHypLoads?.[si] ?? null}
                            prevWeekSetRule={prevWeekSets?.[si]?.progressionRule ?? null}
                            prevSetHypLoad={si > 0 ? (currentWeekHypLoads?.[si - 1] ?? null) : null}
                            prevSetRule={si > 0 ? (sets[si - 1]?.progressionRule ?? null) : null}
                            prevWeekSet={prevWeekSets?.[si] ?? null}
                            prevSetInWeek={si > 0 ? (sets[si - 1] ?? null) : null}
                            hypLoad={currentWeekHypLoads?.[si] ?? null}
                            hypRepsProjected={currentWeekHypReps?.[si] ?? null}
                            tecnicheRaw={tecnicheRaw}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </StrictModeDroppable>

            <div style={{ display: "flex", gap: 8, marginTop: sets.length > 0 ? 8 : 0 }}>
              <button
                type="button"
                className="pp-add-week-btn"
                style={{ flex: 1, marginTop: 0 }}
                onClick={addSet}
              >
                <FontAwesomeIcon icon={faPlus} />
                Aggiungi Set
              </button>
              <button
                type="button"
                className="pp-add-week-btn"
                style={{ flex: 1, marginTop: 0 }}
                onClick={() => setShowUnified((p) => !p)}
              >
                <FontAwesomeIcon icon={faCopy} />
                Set Unificati
              </button>
            </div>

            {showUnified && (
              <div className="pp-unified-panel">
                <div className="pp-unified-panel-header">
                  <span className="pp-unified-panel-title">Configura Set Unificati</span>
                  <button
                    type="button"
                    className="pp-week-delete"
                    onClick={() => setShowUnified(false)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <FieldGroup label="Numero di set da generare">
                    <InputNumber
                      min={1}
                      max={20}
                      value={unifiedN}
                      onChange={(v) => setUnifiedN(v ?? 1)}
                      style={{ width: 110 }}
                    />
                  </FieldGroup>
                </div>
                <ProgressionRuleEditor
                  rule={unifiedRule}
                  isFirstWeek={isFirstWeek}
                  setIndex={0}
                  onChange={(p) => setUnifiedRule((prev) => ({ ...prev, ...p }))}
                  copiedBoxConfig={copiedPrecompiledRuleBox}
                  onCopyBoxConfig={handleCopyBoxConfig}
                />
                <button
                  type="button"
                  className="button-primary"
                  style={{ marginTop: 12 }}
                  onClick={applyUnified}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Genera {unifiedN} {unifiedN === 1 ? "Set" : "Set"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PrecompiledProgressionForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const isEdit = Boolean(id);

  // F3 PreFE — independent week-level clipboard slot. See clipboard.tsx for
  // why precompiled / programmazione clipboards are separate.
  // FE-M (#97065f8b) — full-progression clipboard slot used by the "Incolla
  // progressione" header action: replaces `weeks` but leaves the form's
  // current label/description/tags intact (per the spec).
  const {
    copiedPrecompiledWeek,
    copyPrecompiledWeek,
    copiedPrecompiledProgression,
  } = useClipboard();
  const [pasteProgConfirmOpen, setPasteProgConfirmOpen] = useState(false);

  const [form] = Form.useForm();
  const { options, loading: optLoading } = useEntityOptions();
  const [weeks, setWeeks] = useState<PrecompiledWeek[]>([emptyWeek(1)]);
  const [openWeeks, setOpenWeeks] = useState<Set<number>>(new Set([0]));
  const [saving, setSaving] = useState(false);
  const [loadingProgression, setLoadingProgression] = useState(isEdit);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);

  const [weekRepSchemes, setWeekRepSchemes] = useState<
    Record<number, { label: string; value: string }[]>
  >({});
  const [weekEfforts, setWeekEfforts] = useState<
    Record<number, { label: string; value: string }[]>
  >({});
  // PROG-05 F1b — per-set repScheme/effort catalogs, keyed by weekIdx → setIdx.
  // Loaded on demand when a set's repSchemeTypeId / effortTypeId is set,
  // either by the user picking a type or by the initial-load effect walking
  // `week.sets` after the normalizer has copied week-level → per-set typeIds.
  const [setRepSchemes, setSetRepSchemes] = useState<
    Record<number, Record<number, { label: string; value: string }[]>>
  >({});
  const [setEfforts, setSetEfforts] = useState<
    Record<number, Record<number, { label: string; value: string }[]>>
  >({});

  // PROG-BUGS/ENGINE-3 (Bug A) — reps grid mirrors weekHypLoads so the header
  // pill and the body "Ipotetico (da sorgente)" badge share one source.
  const weekHypReps = useMemo<(number | string | null)[][]>(
    () => computeHypRepsGridShared(weeks),
    [weeks],
  );

  const weekHypLoads = useMemo<(number | null)[][]>(() => {
    // PROG-ENGINE-V3/1 — kept in sync with `computeHypLoadGrid` in
    // ExerciseProgressionEditor.tsx. AC1.5 parity gate: same algorithm at
    // both dispatch sites so library and programmazione views agree.
    const result: (number | null)[][] = [];
    weeks.forEach((week, wi) => {
      const row: (number | null)[] = [];
      const sets = week.sets ?? [];
      sets.forEach((set, si) => {
        const stsRes = resolveSourceSlot(weeks, wi, si, "sts", set.progressionRule);
        const sisRes = resolveSourceSlot(weeks, wi, si, "sis", set.progressionRule);
        let effectiveRule = set.progressionRule;
        const stsHasInheritedContent =
          stsRes != null && stsRes.rule != null && !ruleIsEmpty(stsRes.rule);
        const sisHasInheritedContent =
          sisRes != null && sisRes.rule != null && !ruleIsEmpty(sisRes.rule);
        if (
          ruleIsEmpty(set.progressionRule) &&
          (stsHasInheritedContent || sisHasInheritedContent)
        ) {
          const base =
            (stsHasInheritedContent ? stsRes!.rule : null) ??
            (sisHasInheritedContent ? sisRes!.rule : null) ??
            set.progressionRule;
          effectiveRule = { ...base };
          if (stsHasInheritedContent && sisHasInheritedContent) {
            effectiveRule.loadComputeSource = "both";
          } else if (stsHasInheritedContent) {
            effectiveRule.loadComputeSource = "sts";
          } else if (sisHasInheritedContent) {
            effectiveRule.loadComputeSource = "sis";
          }
        }
        const prevWeekLoadRaw = wi > 0 ? (result[wi - 1]?.[si] ?? null) : null;
        const prevWeekSetRaw = wi > 0 ? (weeks[wi - 1].sets?.[si] ?? null) : null;
        const prevSetLoadRaw = si > 0 ? (row[si - 1] ?? null) : null;
        const prevSetInWeekRaw = si > 0 ? (sets[si - 1] ?? null) : null;
        const stsSourceSet =
          stsRes ? weeks[stsRes.sourceWi]?.sets?.[stsRes.sourceSi] ?? null : null;
        const sisSourceSet =
          sisRes ? weeks[sisRes.sourceWi]?.sets?.[sisRes.sourceSi] ?? null : null;
        // #cda06c24 — source-seed extraction. Used both as the synthetic
        // prev-slot's workLoad AND as the load argument passed to
        // `computeHypLoad` when the offset>1 walk-back path fires, so the
        // axis rule applies ONCE against the seed for every covered slot
        // instead of compounding off the prior link's projection.
        const sourceSeedLoad = (set: PrecompiledSet | null): number | null =>
          set?.workLoad ?? set?.progressionRule?.initialLoad ?? set?.trialLoad ?? null;
        const stsFallbackLoad = sourceSeedLoad(stsSourceSet);
        const sisFallbackLoad = sourceSeedLoad(sisSourceSet);
        const synthPrev = (
          baseSet: PrecompiledSet | null,
          sourceSet: PrecompiledSet | null,
          // `chainLoad` — deprecated by #cda06c24, anchoring is source-seed
          // only (compounding-bug fix). Kept in the signature so the three
          // mirror call sites (#f7275eb3 collapse pending) keep identical
          // shape; intentionally unused here.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          chainLoad: number | null,
          axisRule: PrecompiledWeekProgressionRule | null,
        ): PrecompiledSet | null => {
          // #a6cfa9f3 / #cda06c24 — when the intermediate slot's hypLoad is
          // null (no upstream baseLoad), the synthetic prev anchors to the
          // SOURCE seed (workLoad → initialLoad → trialLoad), NOT to the
          // prior chain link's projection. Anchoring to chainLoad caused
          // compounding (set 0 +5kg, applyForNextSets=2 → set 2 got 70
          // instead of 65). Threshold-mode path via `materializePrevSlot`
          // (#f38a51f4) reads the same source seed → consistent anchor.
          const effectiveLoad = sourceSeedLoad(sourceSet);
          if (effectiveLoad == null) return baseSet;
          return {
            ...(baseSet ?? emptySet()),
            workReps: sourceSet?.workReps ?? baseSet?.workReps ?? null,
            workLoad: effectiveLoad,
            repScheme: sourceSet?.repScheme ?? baseSet?.repScheme ?? null,
            progressionRule: axisRule ?? baseSet?.progressionRule ?? emptyRule(),
          };
        };
        const stsOffset = stsRes?.sourceOffset ?? 0;
        const sisOffset = sisRes?.sourceOffset ?? 0;
        const prevWeekSet =
          stsOffset > 1
            ? synthPrev(prevWeekSetRaw, stsSourceSet, prevWeekLoadRaw, stsRes?.rule ?? null)
            : prevWeekSetRaw;
        const prevSetInWeek =
          sisOffset > 1
            ? synthPrev(prevSetInWeekRaw, sisSourceSet, prevSetLoadRaw, sisRes?.rule ?? null)
            : prevSetInWeekRaw;
        // #cda06c24 / #615019e1 — anchor the load arg to `computeHypLoad` on
        // the source seed whenever a source is resolved, at any offset
        // (including 1). The pre-#615019e1 gate `stsOffset > 1` left
        // offset=1 reading `row[si-1]` which traces to
        // `computeInitialBaseline` and ignores `workLoad` — asymmetric with
        // the offset>1 (synthPrev) path. Unifying both paths anchors rule
        // application to the source seed at every covered offset, so a set
        // 0 with a logged `workLoad` propagates to both set 1 (offset=1)
        // and set 2 (offset=2) consistently. When `seedLoadFromSource`
        // returns null (no seed on source), falls through to the raw
        // chain-link value — preserves the no-seed regression case.
        const seedLoadFromSource = sourceSeedLoad;
        const prevWeekLoad =
          stsRes != null
            ? (seedLoadFromSource(stsSourceSet) ?? prevWeekLoadRaw)
            : prevWeekLoadRaw;
        const prevSetLoad =
          sisRes != null
            ? (seedLoadFromSource(sisSourceSet) ?? prevSetLoadRaw)
            : prevSetLoadRaw;
        const prevWeekSetRule =
          stsRes?.rule ?? prevWeekSetRaw?.progressionRule ?? null;
        const prevSetRule =
          sisRes?.rule ?? prevSetInWeekRaw?.progressionRule ?? null;
        row.push(
          computeHypLoad(
            effectiveRule,
            wi === 0,
            si,
            prevWeekLoad,
            prevWeekSetRule,
            prevSetLoad,
            prevSetRule,
            prevWeekSet,
            prevSetInWeek,
            week.repScheme,
          ),
        );
      });
      result.push(row);
    });
    return result;
  }, [weeks]);

  const loadRepSchemesByType = useCallback((weekIdx: number, typeId: number) => {
    Entities.getRepSchemesByType(typeId).then(({ data }) => {
      const items = (data as any).items ?? [];
      setWeekRepSchemes((prev) => ({
        ...prev,
        [weekIdx]: items.map((r: RepScheme) => ({ label: r.repScheme, value: r.repScheme })),
      }));
    });
  }, []);

  const loadSetRepSchemesByType = useCallback(
    (weekIdx: number, setIdx: number, typeId: number) => {
      Entities.getRepSchemesByType(typeId).then(({ data }) => {
        const items = (data as any).items ?? [];
        const opts = items.map((r: RepScheme) => ({
          label: r.repScheme,
          value: r.repScheme,
        }));
        setSetRepSchemes((prev) => ({
          ...prev,
          [weekIdx]: { ...(prev[weekIdx] ?? {}), [setIdx]: opts },
        }));
      });
    },
    []
  );

  const loadSetEffortsByType = useCallback(
    (weekIdx: number, setIdx: number, typeId: number) => {
      Entities.getEffortsByType(typeId).then(({ data }) => {
        const items = (data as any).items ?? [];
        const opts = items.map((e: Effort) => ({ label: e.effort, value: e.effort }));
        setSetEfforts((prev) => ({
          ...prev,
          [weekIdx]: { ...(prev[weekIdx] ?? {}), [setIdx]: opts },
        }));
      });
    },
    []
  );

  const loadEffortsByType = useCallback((weekIdx: number, typeId: number) => {
    Entities.getEffortsByType(typeId).then(({ data }) => {
      const items = (data as any).items ?? [];
      setWeekEfforts((prev) => ({
        ...prev,
        [weekIdx]: items.map((e: Effort) => ({ label: e.effort, value: e.effort })),
      }));
    });
  }, []);

  useEffect(() => {
    if (!id) return;
    Entities.getPrecompiledProgression(id).then(
      ({ data }) => {
        // F1 back-compat: normalize legacy plans where per-set fields are
        // unset by copying week-level params down. Belt-and-braces over the
        // BE normalizer; idempotent when the BE already did the work.
        const prog = normalizePrecompiledProgression(data.progression);
        form.setFieldsValue({ label: prog.label, description: prog.description });
        setTags(prog.tags ?? []);
        const ws = prog.weeks ?? [];
        setWeeks(ws);
        setOpenWeeks(new Set(ws.map((_, i) => i)));
        ws.forEach((w, i) => {
          if (w.repSchemeTypeId) loadRepSchemesByType(i, w.repSchemeTypeId);
          if (w.effortTypeId) loadEffortsByType(i, w.effortTypeId);
          // PROG-05 F1b — after the normalizer has propagated week-level
          // typeIds onto each set, hydrate the per-set catalogs so the
          // cascading Selects render with the correct filtered options.
          (w.sets ?? []).forEach((s, si) => {
            if (s.repSchemeTypeId) loadSetRepSchemesByType(i, si, s.repSchemeTypeId);
            if (s.effortTypeId) loadSetEffortsByType(i, si, s.effortTypeId);
          });
        });
        setLoadingProgression(false);
      },
      () => {
        showToast("pp-load-err", "Impossibile caricare la progressione", undefined, "danger");
        navigate("/progressioni-precompilate");
      }
    );
  }, [
    id,
    form,
    navigate,
    showToast,
    loadRepSchemesByType,
    loadEffortsByType,
    loadSetRepSchemesByType,
    loadSetEffortsByType,
  ]);

  const toggleWeek = useCallback((index: number) => {
    setOpenWeeks((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  }, []);

  const updateWeek = useCallback((index: number, partial: Partial<PrecompiledWeek>) => {
    setWeeks((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...partial };
      return next;
    });
  }, []);

  const addWeek = useCallback(() => {
    setWeeks((prev) => {
      const newIdx = prev.length;
      setOpenWeeks((o) => new Set(o).add(newIdx));
      return [...prev, emptyWeek(newIdx + 1)];
    });
  }, []);

  // F3 PreFE — paste the clipboard's week onto the target index, overwriting
  // its content. weekNumber is rewritten to match the current position.
  const pasteWeek = useCallback((index: number) => {
    if (!copiedPrecompiledWeek) return;
    const cloned: PrecompiledWeek = JSON.parse(JSON.stringify(copiedPrecompiledWeek));
    cloned.weekNumber = index + 1;
    // FE-P (#0370f830) — sweep each pasted set's rule so threshold rows render.
    (cloned.sets ?? []).forEach((s) => {
      if (s.progressionRule) s.progressionRule = normalizePastedRule(s.progressionRule);
    });
    setWeeks((prev) => {
      const next = [...prev];
      next[index] = cloned;
      return next;
    });
    // Refresh per-week catalog caches if the pasted week's typeIds differ.
    if (cloned.repSchemeTypeId) loadRepSchemesByType(index, cloned.repSchemeTypeId);
    if (cloned.effortTypeId) loadEffortsByType(index, cloned.effortTypeId);
    // PROG-05 F1b — pasted week may carry per-set typeIds; hydrate caches.
    (cloned.sets ?? []).forEach((s, si) => {
      if (s.repSchemeTypeId) loadSetRepSchemesByType(index, si, s.repSchemeTypeId);
      if (s.effortTypeId) loadSetEffortsByType(index, si, s.effortTypeId);
    });
  }, [
    copiedPrecompiledWeek,
    loadRepSchemesByType,
    loadEffortsByType,
    loadSetRepSchemesByType,
    loadSetEffortsByType,
  ]);

  // FE-M (#97065f8b) — paste the full clipboard progression's `weeks` array,
  // replacing the form's current weeks. label/description/tags are NOT
  // touched (per the spec). Per-week + per-set repScheme/effort catalogs are
  // re-hydrated for each pasted week using the same logic as pasteWeek above.
  const pasteProgressionWeeks = useCallback(() => {
    if (!copiedPrecompiledProgression) return;
    const cloned: PrecompiledWeek[] = JSON.parse(
      JSON.stringify(copiedPrecompiledProgression.weeks ?? []),
    );
    const renumbered = cloned.map((w, i) => ({ ...w, weekNumber: i + 1 }));
    // FE-P (#0370f830) — sweep all pasted sets' rules so threshold rows render.
    renumbered.forEach((w) => {
      (w.sets ?? []).forEach((s) => {
        if (s.progressionRule) s.progressionRule = normalizePastedRule(s.progressionRule);
      });
    });
    setWeeks(renumbered);
    setOpenWeeks(new Set(renumbered.length > 0 ? [0] : []));
    setWeekRepSchemes({});
    setWeekEfforts({});
    setSetRepSchemes({});
    setSetEfforts({});
    renumbered.forEach((w, wi) => {
      if (w.repSchemeTypeId) loadRepSchemesByType(wi, w.repSchemeTypeId);
      if (w.effortTypeId) loadEffortsByType(wi, w.effortTypeId);
      (w.sets ?? []).forEach((s, si) => {
        if (s.repSchemeTypeId) loadSetRepSchemesByType(wi, si, s.repSchemeTypeId);
        if (s.effortTypeId) loadSetEffortsByType(wi, si, s.effortTypeId);
      });
    });
    showToast(
      "pp-paste-prog-ok",
      "Progressione incollata",
      "Nome, descrizione e tag della progressione corrente sono stati mantenuti.",
      "success",
    );
  }, [
    copiedPrecompiledProgression,
    loadRepSchemesByType,
    loadEffortsByType,
    loadSetRepSchemesByType,
    loadSetEffortsByType,
    showToast,
  ]);

  const removeWeek = useCallback((index: number) => {
    setWeeks((prev) => {
      const next = prev
        .filter((_, i) => i !== index)
        .map((w, i) => ({ ...w, weekNumber: i + 1 }));

      setOpenWeeks((o) => {
        const updated = new Set<number>();
        o.forEach((k) => {
          if (k < index) updated.add(k);
          else if (k > index) updated.add(k - 1);
        });
        return updated;
      });
      setWeekRepSchemes((r) => {
        const updated: Record<number, { label: string; value: string }[]> = {};
        Object.entries(r).forEach(([k, v]) => {
          const ki = parseInt(k);
          if (ki < index) updated[ki] = v;
          else if (ki > index) updated[ki - 1] = v;
        });
        return updated;
      });
      setWeekEfforts((r) => {
        const updated: Record<number, { label: string; value: string }[]> = {};
        Object.entries(r).forEach(([k, v]) => {
          const ki = parseInt(k);
          if (ki < index) updated[ki] = v;
          else if (ki > index) updated[ki - 1] = v;
        });
        return updated;
      });
      // PROG-05 F1b — same shift for per-set caches; the removed week's whole
      // setIdx → options map is dropped.
      setSetRepSchemes((r) => {
        const updated: typeof r = {};
        Object.entries(r).forEach(([k, v]) => {
          const ki = parseInt(k);
          if (ki < index) updated[ki] = v;
          else if (ki > index) updated[ki - 1] = v;
        });
        return updated;
      });
      setSetEfforts((r) => {
        const updated: typeof r = {};
        Object.entries(r).forEach(([k, v]) => {
          const ki = parseInt(k);
          if (ki < index) updated[ki] = v;
          else if (ki > index) updated[ki - 1] = v;
        });
        return updated;
      });
      return next;
    });
  }, []);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagInput("");
  };

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (source.droppableId === "weeks") {
      const from = source.index;
      const to = destination.index;
      setWeeks((prev) => {
        const next = [...prev];
        const [removed] = next.splice(from, 1);
        next.splice(to, 0, removed);
        return next.map((w, i) => ({ ...w, weekNumber: i + 1 }));
      });
      setOpenWeeks((prev) => remapSet(prev, from, to));
      setWeekRepSchemes((prev) => remapRecord(prev, from, to));
      setWeekEfforts((prev) => remapRecord(prev, from, to));
      // PROG-05 F1b — reorder per-set caches alongside their weeks.
      setSetRepSchemes((prev) => remapRecord(prev, from, to));
      setSetEfforts((prev) => remapRecord(prev, from, to));
    } else if (source.droppableId.startsWith("sets-")) {
      const weekIndex = parseInt(source.droppableId.slice("sets-".length));
      const from = source.index;
      const to = destination.index;
      setWeeks((prev) => {
        const next = [...prev];
        const s = [...(next[weekIndex].sets ?? [])];
        const [removed] = s.splice(from, 1);
        s.splice(to, 0, removed);
        next[weekIndex] = { ...next[weekIndex], sets: s };
        return next;
      });
      // PROG-05 F1b — remap per-set catalog keys within the week to follow the
      // setIdx reorder so each SetMiniCard keeps its filtered options.
      setSetRepSchemes((prev) => {
        const inner = prev[weekIndex];
        if (!inner) return prev;
        return { ...prev, [weekIndex]: remapRecord(inner, from, to) };
      });
      setSetEfforts((prev) => {
        const inner = prev[weekIndex];
        if (!inner) return prev;
        return { ...prev, [weekIndex]: remapRecord(inner, from, to) };
      });
    }
  }, []);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const body: Omit<PrecompiledProgression, "id"> = {
        label: values.label,
        description: values.description || null,
        tags: tags.length ? tags : null,
        weeks,
      };
      const promise = isEdit
        ? Entities.updatePrecompiledProgression(id!, body)
        : Entities.createPrecompiledProgression(body);

      promise.then(
        (res) => {
          setSaving(false);
          showToast(
            "pp-save-ok",
            `Progressione ${isEdit ? "aggiornata" : "creata"} correttamente`,
            undefined,
            "success"
          );
          /* T1.B (#8fa535fd) — stay on page after save. On CREATE flip the URL
             to /:newId/modifica so subsequent saves are update-mode (no
             duplicate-create on second Salva click). On UPDATE keep URL. */
          if (!isEdit) {
            const newId = (res?.data as { progression?: { id?: string | number } } | undefined)
              ?.progression?.id;
            if (newId != null) {
              navigate(`/progressioni-precompilate/${newId}/modifica`, {
                replace: true,
              });
            }
          }
        },
        () => {
          setSaving(false);
          showToast("pp-save-err", "Errore nel salvataggio", undefined, "danger");
        }
      );
    } catch {
      /* antd validation highlights fields */
    }
  };

  if (loadingProgression || optLoading) {
    return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={80} width={80} color="#9c1419" radius="1" />
      </div>
    );
  }

  return (
    <>
      {/* ── Sticky bar ── */}
      <div className="pp-form-bar">
        <div className="d-flex align-items-center gap-2 overflow-hidden">
          <button
            className="pp-form-bar-back"
            type="button"
            onClick={() => navigate("/progressioni-precompilate")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <p className="pp-form-bar-title">
            {isEdit ? "Modifica Progressione" : "Nuova Progressione Precompilata"}
          </p>
        </div>
        <div className="pp-form-bar-actions">
          <span className="pp-form-bar-actions-meta">
            {weeks.length} {weeks.length === 1 ? "settimana" : "settimane"}
          </span>
          {copiedPrecompiledProgression && (
            <button
              type="button"
              className="button-secondary"
              title="Incolla progressione"
              onClick={() => setPasteProgConfirmOpen(true)}
            >
              <FontAwesomeIcon icon={faPaste} />
              <span className="ms-2">Incolla progressione</span>
            </button>
          )}
          <button
            className="button-primary pp-form-save-cta"
            onClick={handleSubmit}
            disabled={saving}
            title="Salva"
          >
            {saving ? (
              <TailSpin width={18} height={18} color="#fff" />
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span className="ms-2">Salva</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* FE-M (#97065f8b) — confirm replacing the entire `weeks` array. */}
      <BsModal
        show={pasteProgConfirmOpen}
        onHide={() => setPasteProgConfirmOpen(false)}
      >
        <BsModal.Header closeButton>
          <BsModal.Title style={{ fontSize: "1rem" }}>
            Incolla progressione
          </BsModal.Title>
        </BsModal.Header>
        <BsModal.Body>
          <p>
            Stai per sovrascrivere tutte le settimane di questa progressione.
            Continuare?
          </p>
          <Alert variant="warning" className="py-2 mb-0">
            <span className="small">
              Verranno mantenuti nome, descrizione e tag attuali. Solo le
              settimane saranno sostituite.
            </span>
          </Alert>
        </BsModal.Body>
        <BsModal.Footer>
          <BsButton
            variant="light"
            size="sm"
            className="border"
            onClick={() => setPasteProgConfirmOpen(false)}
          >
            Annulla
          </BsButton>
          <BsButton
            variant="primary"
            size="sm"
            onClick={() => {
              pasteProgressionWeeks();
              setPasteProgConfirmOpen(false);
            }}
          >
            Sovrascrivi
          </BsButton>
        </BsModal.Footer>
      </BsModal>

      <DragDropContext onDragEnd={handleDragEnd}>
      <Container fluid="md" className="pb-5 pt-4 sticky-save-bar-spacer">
        {/* ── Info card ── */}
        <div className="pp-info-card">
          <div className="pp-section-label">
            Informazioni generali
          </div>
          <Form form={form} layout="vertical">
            <Row className="g-3">
              <Col md={5}>
                <Form.Item
                  label="Nome"
                  name="label"
                  rules={[{ required: true, message: "Il nome è obbligatorio" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Nome della progressione..." size="large" />
                </Form.Item>
              </Col>
              <Col md={7}>
                <Form.Item
                  label="Descrizione"
                  name="description"
                  style={{ marginBottom: 0 }}
                >
                  <Input.TextArea
                    rows={2}
                    placeholder="Descrizione opzionale..."
                    style={{ resize: "none" }}
                  />
                </Form.Item>
              </Col>
              <Col md={12}>
                <label
                  className="ant-form-item-label"
                  style={{ fontSize: "0.85rem", fontWeight: 500, display: "block", marginBottom: 4 }}
                >
                  Tag
                </label>
                <div
                  className="pp-tag-area"
                  onClick={() =>
                    (document.getElementById("pp-tag-input") as HTMLInputElement | null)?.focus()
                  }
                >
                  {tags.map((t) => (
                    <Tag
                      key={t}
                      closable
                      onClose={() => setTags((prev) => prev.filter((x) => x !== t))}
                      color="blue"
                      style={{ margin: 0 }}
                    >
                      {t}
                    </Tag>
                  ))}
                  <input
                    id="pp-tag-input"
                    className="pp-tag-input"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    onBlur={addTag}
                    placeholder={tags.length === 0 ? "Aggiungi tag e premi Invio..." : ""}
                  />
                </div>
              </Col>
            </Row>
          </Form>
        </div>

        {/* ── Weeks ── */}
        <div className="pp-weeks-header">
          <div>
            <p className="pp-weeks-title">
              <FontAwesomeIcon icon={faRepeat} className="me-2" style={{ color: "var(--app-primary)" }} />
              Settimane
            </p>
          </div>
          <span className="pp-weeks-count">{weeks.length} configurate</span>
        </div>

        <StrictModeDroppable droppableId="weeks">
          {(droppableProvided) => (
            <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
              {weeks.map((week, i) => (
                <Draggable key={i} draggableId={`week-${i}`} index={i}>
                  {(draggableProvided) => (
                    <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                      <WeekCard
                        week={week}
                        weeks={weeks}
                        index={i}
                        total={weeks.length}
                        open={openWeeks.has(i)}
                        onToggle={toggleWeek}
                        onUpdate={updateWeek}
                        onRemove={removeWeek}
                        onCopy={(idx) => copyPrecompiledWeek(weeks[idx])}
                        onPaste={pasteWeek}
                        canPaste={!!copiedPrecompiledWeek}
                        repSchemeOptions={weekRepSchemes[i] ?? []}
                        effortOptions={weekEfforts[i] ?? []}
                        tutOptions={options.tuts}
                        tecnicaOptions={options.tecniche}
                        tecnicheRaw={options.tecnicheRaw}
                        repSchemeTypeOptions={options.repSchemeTypes}
                        effortTypeOptions={options.effortTypes}
                        onRepSchemeTypeChange={loadRepSchemesByType}
                        onEffortTypeChange={loadEffortsByType}
                        setRepSchemes={setRepSchemes[i]}
                        setEfforts={setEfforts[i]}
                        onSetRepSchemeTypeChange={loadSetRepSchemesByType}
                        onSetEffortTypeChange={loadSetEffortsByType}
                        dragHandleProps={draggableProvided.dragHandleProps}
                        prevWeekHypLoads={i > 0 ? weekHypLoads[i - 1] : undefined}
                        currentWeekHypLoads={weekHypLoads[i]}
                        prevWeekSets={i > 0 ? weeks[i - 1].sets ?? null : null}
                        currentWeekHypReps={weekHypReps[i]}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </div>
          )}
        </StrictModeDroppable>

        <button className="pp-add-week-btn" type="button" onClick={addWeek}>
          <FontAwesomeIcon icon={faPlus} />
          Aggiungi Settimana
        </button>
      </Container>
      </DragDropContext>

      <StickySaveBar onSave={handleSubmit} saving={saving} />
    </>
  );
}
